
import { Shield, Target, FileSearch, Bug, Lock, Zap } from "lucide-react";
import { LandingPageTemplate } from "@/components/layout/LandingPageTemplate";
import { HeroSection } from "@/components/landing/HeroSection";
import { FeatureSection } from "@/components/landing/FeatureSection";
import { ProcessSection } from "@/components/landing/ProcessSection";
import { BenefitSection } from "@/components/landing/BenefitSection";
import { CtaSection } from "@/components/landing/CtaSection";

const PenetrationTesting = () => {
  return (
    <LandingPageTemplate
      title="Penetration Testing Services"
      description="Expert penetration testing services to identify and address security vulnerabilities in your systems and applications."
      keywords={[
        "penetration testing",
        "security testing",
        "vulnerability assessment",
        "security audit",
        "ethical hacking"
      ]}
      canonicalUrl="/cybersecurity/penetration-testing/"
      schemaType="service"
      schemaData={{
        name: "Penetration Testing Services",
        description: "Professional penetration testing and vulnerability assessment",
        provider: {
          name: "edgefi",
          sameAs: "https://edgefi.com"
        }
      }}
    >
      <HeroSection
        title="Uncover Security"
        highlighted="Vulnerabilities"
        description="Professional penetration testing services to identify and remediate security vulnerabilities before they can be exploited."
        ctaText="Start Assessment"
        heroPattern="grid"
      />

      <FeatureSection 
        title="Comprehensive Testing Services"
        subtitle="TESTING CAPABILITIES"
        description="Our penetration testing services cover all aspects of your infrastructure."
        features={[
          {
            title: "Network Testing",
            description: "In-depth testing of network infrastructure and segmentation.",
            icon: <Shield className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Application Testing",
            description: "Web and mobile application vulnerability assessment.",
            icon: <Target className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Code Review",
            description: "Secure code review and analysis.",
            icon: <FileSearch className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Bug Hunting",
            description: "Proactive vulnerability discovery and reporting.",
            icon: <Bug className="h-6 w-6 text-purple-600" />
          },
          {
            title: "API Security",
            description: "API endpoint testing and validation.",
            icon: <Lock className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Quick Response",
            description: "Rapid reporting and remediation guidance.",
            icon: <Zap className="h-6 w-6 text-purple-600" />
          }
        ]}
      />

      <ProcessSection
        title="Our Testing Process"
        subtitle="METHODOLOGY"
        description="A systematic approach to discovering and addressing vulnerabilities"
        steps={[
          {
            title: "Scoping",
            description: "Define testing boundaries and objectives"
          },
          {
            title: "Reconnaissance",
            description: "Gather intelligence about target systems"
          },
          {
            title: "Testing",
            description: "Execute comprehensive security tests"
          },
          {
            title: "Analysis",
            description: "Evaluate findings and assess risks"
          },
          {
            title: "Reporting",
            description: "Detailed vulnerability reporting"
          }
        ]}
      />

      <BenefitSection
        title="Why Choose Our Testing Services"
        benefits={[
          {
            title: "Expert Team",
            description: "Certified security professionals"
          },
          {
            title: "Comprehensive Coverage",
            description: "Full-scope security assessment"
          },
          {
            title: "Clear Reports",
            description: "Actionable findings and recommendations"
          },
          {
            title: "Quick Turnaround",
            description: "Efficient testing process"
          },
          {
            title: "Support",
            description: "Post-assessment remediation guidance"
          },
          {
            title: "Compliance",
            description: "Meet regulatory requirements"
          }
        ]}
      />

      <CtaSection 
        title="Ready to Test Your Security?"
        description="Schedule a penetration test to identify vulnerabilities in your systems."
        ctaText="Schedule Testing"
        background="purple"
        modalTitle="Request Penetration Testing"
        modalDescription="Tell us about your security testing needs."
        additionalFields={{
          lead_source: "Website",
          lead_interest: "Penetration Testing",
          service_interest: "Security Assessment"
        }}
      />
    </LandingPageTemplate>
  );
};

export default PenetrationTesting;
